import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/icon.js';
import type { GameProvider } from '@/features/games/api.js';
import { providerBadgeCodeOf } from '@/features/games/brandBadge.js';
import { ProviderArt } from './ProviderArt.js';

interface ProviderLogoRowProps {
  title: string;
  providers: GameProvider[];
  onSelect: (id: string) => void;
  /** Opens the full list of camps; the button shows only when given. */
  onAll?: () => void;
}

/** Duel-style horizontal scroller of 3:4 camp art cards, named inside like game cards. */
export function ProviderLogoRow({ title, providers, onSelect, onAll }: ProviderLogoRowProps) {
  const { t } = useTranslation('common');
  const scrollerRef = useRef<HTMLDivElement>(null);

  function scrollBy(dir: number) {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
  }

  if (providers.length === 0) return null;

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Icon name="grid" size={18} className="text-[var(--text-secondary)]" />
          <h2 className="text-[17px] md:text-xl font-bold tracking-tight truncate">{title}</h2>
          <span className="font-mono text-[11px] text-[var(--text-muted)] hidden sm:inline">{providers.length}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => scrollBy(-1)}
            className="w-8 h-8 rounded-full bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] grid place-items-center text-[var(--text-secondary)] transition-colors"
            aria-label={t('common.previous')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button
            onClick={() => scrollBy(1)}
            className="w-8 h-8 rounded-full bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] grid place-items-center text-[var(--text-secondary)] transition-colors"
            aria-label={t('common.next')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
          </button>
          {onAll && (
            <button
              type="button"
              onClick={onAll}
              className="ml-1 h-8 px-3 rounded-full bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] text-[12px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              {t('common.viewAll')} →
            </button>
          )}
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto bf-noscroll snap-x scroll-px-4 -mx-4 px-4 py-2 -my-2 md:mx-0 md:px-0 md:scroll-px-0"
      >
        {providers.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            className="group shrink-0 snap-start w-[112px] md:w-[136px] rounded-2xl text-left outline-none"
            title={p.displayName}
          >
            <ProviderArt
              name={p.displayName}
              logoUrl={p.logoUrl}
              brandColor={p.brandColor}
              badgeCode={providerBadgeCodeOf(p.code)}
              nameClassName="text-[14px]"
              className="aspect-[3/4] w-full rounded-2xl ring-1 ring-[var(--border-strong)] transition-[transform,box-shadow] duration-200 ease-out group-hover:scale-[1.04] group-hover:ring-[var(--accent)] group-focus-visible:ring-2 group-focus-visible:ring-[var(--primary)]"
            />
          </button>
        ))}
      </div>
    </section>
  );
}
